import { useEffect } from "react";
import { CLINIC } from "@/data/clinic";

const SITE_ORIGIN = "https://drashutoshmishra.in";

function setMeta(name, content, attr = "name") {
  if (!content) return;
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel, href) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function upsertJSONLD(id, data) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

function removeJSONLD(id) {
  const el = document.getElementById(id);
  if (el) el.remove();
}

/**
 * SEOHead - applies title/description/canonical and optional JSON-LD schema blocks per page.
 * Props:
 *  - title
 *  - description
 *  - path (e.g. "/diabetes-treatment-varanasi")
 *  - image (OG image)
 *  - schemas: array of {id, data} JSON-LD blocks
 *  - breadcrumbs: array of {name, url}
 *  - faqs: array of {q, a}
 */
export default function SEOHead({
  title,
  description,
  path = "/",
  image = CLINIC.doctorPhoto,
  schemas = [],
  breadcrumbs = [],
  faqs = [],
}) {
  useEffect(() => {
    if (title) document.title = title;
    setMeta("description", description);
    setMeta("og:title", title, "property");
    setMeta("og:description", description, "property");
    setMeta("og:image", image, "property");
    setMeta("og:url", `${SITE_ORIGIN}${path}`, "property");
    setMeta("og:type", "website", "property");
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);
    setMeta("twitter:image", image);
    setLink("canonical", `${SITE_ORIGIN}${path}`);

    // Breadcrumb schema
    if (breadcrumbs.length) {
      upsertJSONLD("ld-breadcrumb", {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: breadcrumbs.map((b, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: b.name,
          item: `${SITE_ORIGIN}${b.url}`,
        })),
      });
    } else {
      removeJSONLD("ld-breadcrumb");
    }

    // FAQ schema
    if (faqs.length) {
      upsertJSONLD("ld-faq", {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      });
    } else {
      removeJSONLD("ld-faq");
    }

    schemas.forEach((s) => upsertJSONLD(s.id, s.data));

    return () => {
      schemas.forEach((s) => removeJSONLD(s.id));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, path, image]);

  return null;
}

/* Reusable schema builders */
export const physicianSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Physician",
  name: CLINIC.doctorName,
  alternateName: "Dr Ashutosh Mishra",
  image: CLINIC.doctorPhoto,
  url: SITE_ORIGIN,
  telephone: `+91${CLINIC.phone}`,
  email: CLINIC.email,
  medicalSpecialty: ["Endocrinology", "Diabetology", "Internal Medicine"],
  address: {
    "@type": "PostalAddress",
    streetAddress: CLINIC.street,
    addressLocality: CLINIC.city,
    addressRegion: CLINIC.region,
    postalCode: CLINIC.pincode,
    addressCountry: CLINIC.country,
  },
  geo: { "@type": "GeoCoordinates", latitude: CLINIC.geo.lat, longitude: CLINIC.geo.lng },
  hasCredential: [
    "MBBS",
    "MD (Medicine), IMS BHU",
    "DFID, CMC Vellore",
    "DMSc Endocrinology, University of South Wales (UK)",
  ],
});

export const localBusinessSchema = () => ({
  "@context": "https://schema.org",
  "@type": "MedicalClinic",
  "@id": `${SITE_ORIGIN}#clinic`,
  name: "Dr. Ashutosh Mishra — Diabetes & Endocrinology Clinic",
  image: CLINIC.doctorPhoto,
  url: SITE_ORIGIN,
  telephone: `+91${CLINIC.phone}`,
  email: CLINIC.email,
  priceRange: "₹₹",
  address: {
    "@type": "PostalAddress",
    streetAddress: CLINIC.street,
    addressLocality: CLINIC.city,
    addressRegion: CLINIC.region,
    postalCode: CLINIC.pincode,
    addressCountry: CLINIC.country,
  },
  geo: { "@type": "GeoCoordinates", latitude: CLINIC.geo.lat, longitude: CLINIC.geo.lng },
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
      opens: "10:00",
      closes: "20:30",
    },
  ],
  hasMap: CLINIC.mapLink,
});

export const reviewAggregateSchema = (testimonials = []) => {
  if (!testimonials.length) return null;
  const avg = (testimonials.reduce((a, t) => a + (t.rating || 5), 0) / testimonials.length).toFixed(1);
  return {
    "@context": "https://schema.org",
    "@type": "MedicalBusiness",
    "@id": `${SITE_ORIGIN}#clinic-reviews`,
    name: "Dr. Ashutosh Mishra — Diabetes & Endocrinology Clinic",
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: avg,
      reviewCount: String(testimonials.length),
      bestRating: "5",
      worstRating: "1",
    },
    review: testimonials.slice(0, 5).map((t) => ({
      "@type": "Review",
      author: { "@type": "Person", name: t.name },
      reviewRating: { "@type": "Rating", ratingValue: String(t.rating || 5), bestRating: "5" },
      reviewBody: t.quote,
    })),
  };
};
